<template>
  <div class="">
    <ToggleSwitch
      :model-value="isVattable"
      :options="vattableOptions"
      class="mb-2 max-w-48"
      @update:model-value="handleVattableSwitch"
    />

    <template v-if="isVattable">
      <div class="flex space-x-2">
        <template v-for="rate in defaultRates" :key="rate">
          <button
            type="button"
            :class="[
              inputValue === rate
                ? 'bg-primary text-primary-fg'
                : '',
              'w-16 rounded-md border border-background-fg/6 px-3 py-2 font-semibold',
            ]"
            @click="setVatRate(rate)"
          >
            {{ rate }}%
          </button>
        </template>
        <div class="flex items-center space-x-2">
          <input
            type="number"
            :name="name"
            :class="[
              'block w-full max-w-24 flex-1 rounded-md border border-background-fg/8 text-base font-semibold focus:outline-none', // Font size set at >14px (text-base) so no zoom on mobile when focused
              hasError
                ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
                : 'focus:border-primary focus:ring-primary',
            ]"
            :value="inputValue"
            :aria-invalid="!!errorMessage"
            enterKeyHint="done"
            @input="handleVatTextInput"
            @blur="handleBlur"
            @keydown.backspace="handleBackspace"
            @keydown.enter="handleEnter"
          />
          <span class="text-lg font-semibold">%</span>
        </div>
      </div>
    </template>
    <template v-if="errorMessage">
      <p class="mt-1 text-xs font-medium text-error">
        {{ errorMessage }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate"
const props = defineProps<{
  name: string
  label: string
}>()

const { isUKmerchant } = useTenant()

const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
} = useField<number | null>(toRef(props, "name"))

const hasError = computed(() => !!errorMessage.value)

const isVattable = computed(
  () =>
    inputValue.value !== null &&
    inputValue.value !== undefined
)

const setVatRate = (value: number | null) => {
  handleChange(value)
}

const defaultRates = computed(() =>
  isUKmerchant.value ? [0, 5, 20] : [0, 13.5, 23]
)

const vattableOptions = [
  {
    value: true,
    label: "VAT",
  },
  {
    value: false,
    label: "No VAT",
  },
] as const

const handleVattableSwitch = (value: boolean) => {
  if (value) {
    handleChange(0)
  } else {
    handleChange(null)
  }
}

const handleVatTextInput = (event: Event) => {
  const value = (event?.target as HTMLInputElement)
    ?.valueAsNumber

  if (!value) {
    handleChange(0)
    return
  }

  handleChange(value)
}

const handleBackspace = (event: KeyboardEvent) => {
  if (inputValue.value === 0) {
    event.preventDefault()
  }
}

const handleEnter = (event: Event) =>
  (event?.target as HTMLInputElement)?.blur()
</script>
